import React from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import {
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Gavel as GavelIcon,
  Timeline as TimelineIcon,
  Insights as InsightsIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Digital Trust page component
 *
 * This page explains Laprecio's approach to digital trust in mineral trading
 */
const DigitalTrustPage = () => {
  return (
    <>
      <Helmet>
        <title>Digital Trust | Laprecio - Global Trade, Digital Trust</title>
        <meta name="description" content="Learn how Laprecio ensures digital trust through blockchain verification, secure transactions, and transparent supply chain tracking for ethical mineral trading." />
        <meta name="keywords" content="digital trust, blockchain verification, mineral traceability, secure transactions, ethical sourcing, supply chain transparency" />
        <link rel="canonical" href="https://laprecio.com/digital-trust" />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.alternate',
          py: 8,
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h1" align="center" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Digital Trust
          </Typography>
          <Typography variant="h2" align="center" color="primary" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', md: '2rem' }, mb: 2 }}>
            Global Trade, Digital Trust
          </Typography>
          <Typography variant="h3" align="center" paragraph sx={{ maxWidth: 800, mx: 'auto', mb: 4, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            Our platform ensures transparency, security, and compliance at every step of the mineral supply chain
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        {/* Introduction */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            What is Digital Trust?
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
            Digital Trust is the foundation of Laprecio's mineral trading platform. It represents our commitment to creating a secure, transparent, and reliable ecosystem where all stakeholders can trade with confidence. Through advanced technologies and rigorous verification processes, we ensure that every transaction on our platform is trustworthy, traceable, and compliant with international standards.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
            In the minerals industry, where concerns about ethical sourcing, environmental impact, and regulatory compliance are paramount, digital trust is not just a feature—it's a necessity. Laprecio's platform is built from the ground up to address these concerns and provide a trading environment where trust is systematically established and maintained.
          </Typography>
        </Box>

        {/* Key Components */}
        <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
          Key Components of Our Digital Trust Framework
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: '12px',
                border: '1px solid #f5e5a5',
                borderLeft: '6px solid #ca8a04',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ color: 'primary.main', fontSize: 40, mr: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  Blockchain Verification
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                Our platform leverages blockchain technology to create immutable records of mineral origins, transactions, and certifications. This ensures:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Tamper-proof documentation of mineral provenance" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Permanent record of all transactions and certifications" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Transparent chain of custody from mine to market" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: '12px',
                border: '1px solid #f5e5a5',
                borderLeft: '6px solid #ca8a04',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VerifiedIcon sx={{ color: 'primary.main', fontSize: 40, mr: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  Verified Authenticity
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                We implement rigorous verification processes to ensure the authenticity of all minerals traded on our platform:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Independent verification of mineral sources" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Documentation of extraction methods and conditions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Certification of ethical sourcing practices" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: '12px',
                border: '1px solid #f5e5a5',
                borderLeft: '6px solid #ca8a04',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GavelIcon sx={{ color: 'primary.main', fontSize: 40, mr: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  Regulatory Compliance
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                Our platform ensures compliance with international regulations and standards:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Adherence to EU Conflict Minerals Regulation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Compliance with OECD Due Diligence Guidance" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Support for US Dodd-Frank Act requirements" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                height: '100%',
                borderRadius: '12px',
                border: '1px solid #f5e5a5',
                borderLeft: '6px solid #ca8a04',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimelineIcon sx={{ color: 'primary.main', fontSize: 40, mr: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  Supply Chain Transparency
                </Typography>
              </Box>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                We provide complete visibility into the mineral supply chain:
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Real-time tracking of minerals from source to buyer" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Documentation of all handling and processing steps" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Verification of ethical practices throughout the supply chain" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        {/* Benefits Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            Benefits of Digital Trust
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    For Miners & Producers
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Premium pricing for verified ethical minerals" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Direct access to global markets" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Recognition for ethical mining practices" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    For Manufacturers
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Verified ethical sourcing for ESG compliance" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Simplified regulatory reporting" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Enhanced brand reputation" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    For Traders & Brokers
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Access to verified ethical minerals" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Reduced due diligence costs" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Secure and efficient transactions" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'background.alternate', borderRadius: 4 }}>
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Experience Digital Trust in Mineral Trading
          </Typography>
          <Typography variant="h3" paragraph sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
            Join our platform and transform how you trade minerals
          </Typography>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            color="primary"
            sx={{ px: 5, py: 1.5, borderRadius: 50, fontSize: '1.1rem', fontWeight: 'bold' }}
          >
            Register Interest
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default DigitalTrustPage;
